import { Injectable } from '@angular/core';
import { CdkDragDrop, moveItemInArray, transferArrayItem} from '@angular/cdk/drag-drop';
import { BehaviorSubject, Observable, Subject, forkJoin, of } from 'rxjs';
import { skipWhile, takeUntil } from 'rxjs/operators';
import { v4 as createId } from 'uuid';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { StudioFormConfig, StudioFormInputElementTypes, StudioFormInputTypesMap, studioFormInputTypesWithVariablePool, variablePoolPaths } from 'src/app/connector-studio/models/inputElementTypes.model';
import { filter as _filter, findIndex as _findIndex, pick as _pick,
mergeWith as _mergeWith, forEach as _forEach, get as _get, castArray as _castArray,
remove as _remove, toString as _toString, keys as _keys, cloneDeep as _cloneDeep, 
first as _first, map as _map, set as _set, indexOf as _indexOf , sortBy as _sortBy, omit as _omit,
groupBy as _groupBy, isArray as _isArray, concat as _concat, has as _has, 
isUndefined as _isUndefined, compact as _compact, includes as _includes, values as _values, 
slice as _slice, isEmpty as _isEmpty, size as _size} from 'lodash-es';
import { put } from 'src/app/connector-studio/utilities/helpers';
import { stepAutoAddedFieldsMap } from '../../models/inputElementProperties.model'
import { environment } from 'src/environments/environment';
import { StepsBuilderDataService } from '../steps-builder-data/steps-builder-data.service';
import { AppService } from 'src/app/app.service';
import { Step, StepTypes } from '../../interfaces/stepGraph';
import Drawflow from 'src/app/connector-studio/library/drawflow';
import { ActivatedRoute } from '@angular/router';
const DISABLE_POLLING = true;


@Injectable({
  providedIn: 'root'
})
export class StepsBuilderHelperService {
  copyFromInputs: StudioFormConfig[] = [...StudioFormInputElementTypes];
  studioInputs: Map<any, any> = StudioFormInputTypesMap();
  dropInputs: any = [];
  private _fields$ = new BehaviorSubject<any>(this.dropInputs);
  fields$ = this._fields$.asObservable();
  private _stepGraphs$ = new Subject<any[]>();
  public stepGraphs$ = this._stepGraphs$.asObservable();
  private _validateEST$ = new Subject<any[]>();
  public validateEST$ = this._validateEST$.asObservable();
  private _addStepGraphs$ = new Subject<any[]>();
  public addStepGraphs$ = this._addStepGraphs$.asObservable();
  private _currentActiveField$ = new Subject<StudioFormConfig | null>();
  currentActiveField$ = this._currentActiveField$.asObservable();
  private _fieldDeleteEvent$ = new Subject<any>();
  fieldDeleteEvent$ = this._fieldDeleteEvent$.asObservable();
  private _stepChangeEvent$ = new Subject<any>();
  stepChangeEvent$ = this._stepChangeEvent$.asObservable();
  private _changeStep$ = new BehaviorSubject<any>(null);
  changeStep$ = this._changeStep$.asObservable();
  private _saveStepEvent$ = new Subject<any>();
  saveStepEvent$ = this._saveStepEvent$.asObservable();
  private _builderClickEvent$ = new Subject<any>();
  builderClickEvent$ = this._builderClickEvent$.asObservable();
  private _saveSourceInfoEvent$ = new Subject<any>();
  saveSourceInfoEvent$ = this._saveSourceInfoEvent$.asObservable();
  stepInputElementParams: any = {};
  stepInputElementOrder: any = {};
  temporaryStepGraphFields: {
    [key: string]: StudioFormConfig[]
  } = {}

  currentActiveField: any;
  currentActiveFieldId: string;
  stepGraphs: any = [];
  currentStepIndex: number;
  currentStepNodeId: string; 
  private editor: Drawflow;
  isInputElementListOpen: boolean = true;
  private unsubscribe$ = new Subject<boolean>();
  public variablePool = {};
  public isStepBeingSaved: boolean;
  public isStepSaved: boolean;
  public isStepBeingCreated: boolean;
  public isStepBeingDeleted: boolean;
  public isStepsLoaded: boolean;
  public isSourceInfoStepValid: boolean;
  public isSourceInfoSaved: boolean;
  public isSourceInfoBeingSaved: boolean;
  public stepGraphEvents = {};
  private _stepGraphEvents$ = new BehaviorSubject<any>({});
  public readonly stepGraphEvents$: Observable<any> = this._stepGraphEvents$.asObservable()
    .pipe(skipWhile(data => data === undefined || data === null));
  public isSaving$ = new BehaviorSubject<boolean>(false);
  public readonly isSaving: Observable<any> = this.isSaving$.asObservable()
  private dataSourceType: string;
  private studioInfoData: any = {};
  private updatedVariablePool: any = {};

  constructor(private stepsBuilderDataService: StepsBuilderDataService, 
    private appService: AppService, private snackBar: MatSnackBar, private route: ActivatedRoute) { }

  public stepDataIdMap = new Map();
  public stepFieldIdMap = new Map();

  public get isCommandsPollingDisabled() : boolean {
    return DISABLE_POLLING;
  }

  public get editorInstance() : Drawflow {
    return this.editor
  }

  public set setEditor(editor : Drawflow) {
    this.editor = editor;
  }
  
  get studioSourceType() {
    return this.dataSourceType;
  }

  set setStudioSourceType(sourceType: string) {
    this.dataSourceType = sourceType;
  }

  get getCurrentStepIndex() {
    return this.currentStepIndex;
  }

  public get userId() : number {
    return this.appService.userData.userId;
  }

  get getCurrentStepNodeId() {
    return this.currentStepNodeId;
  }

  set setCurrentStepNodeId(nodeId: string) {
    this.currentStepNodeId = nodeId;
  }

  public get getStepGraphEvents() : any {
    return this.stepGraphEvents
  }
  
  set setStepGraphEvents(edges: any) {
    this.stepGraphEvents = edges;
  }
  
  setVariablePoolData(data) {
    this.updatedVariablePool = data;
  }

  uploadStepGraphCommands(userId: number, sourceType: string, data: any) {
    const graphKey = `${this.userId}.${this.studioSourceType}`
    put(this.stepGraphEvents, [`${graphKey}[]`], data)
    this._stepGraphEvents$.next({ stepGraphEvents: this.stepGraphEvents });
    return of('done');
  }

  saveStepGraphData() {
    const tableKey = `${this.userId}.${this.studioSourceType}`
    const requests =  _compact(_map(_get(this.stepGraphEvents,tableKey), item => {
      const data = item
      let userId = this.userId;
      let sourceType = this.studioSourceType;
      if (!_isEmpty(data)) {
        return this.stepsBuilderDataService.updateStepGraph({userId, sourceType, data})
      }
      return undefined
    }))
    this.isSaving$.next(true);
    forkJoin([...requests])
      .subscribe({
        next: (response) => {
          this.setStepGraphEvents = {};
          this._stepGraphEvents$.next({ stepGraphEvents: this.stepGraphEvents });
          this.snackBar.open(
            'Saved successfully', '',
            {duration: 2500, verticalPosition: 'top',horizontalPosition: 'center', panelClass: ['warning-snack-bar']});
            this.isSaving$.next(false);
          },
        error: (error) => {
          const errorMessage = _get(error, 'error.message')
          this.isSaving$.next(false);
          this.snackBar.open(
            errorMessage || 'Unable to save', '',
            {duration: 2500, verticalPosition: 'top',horizontalPosition: 'center', panelClass: ['warning-snack-bar']});
        },
        complete: () => {
          this.isSaving$.next(false);
        }
      })

  }

  initStepGraph(data) {
    this.isStepSaved = true;
    const nodes = _get(data, 'nodes', []);
    this.variablePool = _get(data, 'variablePool', {});
    this.stepGraphs  = [...nodes]
    this.isStepsLoaded = true;
    this.setCurrentStepNodeId = _get(_first(nodes), 'nodeId');
    _forEach(this.stepGraphs, (step, index) => {
      if (_get(step,'nodeType') === 'FIRST_STEP_NODE' && !_get(step, 'params.inputElements')) {
        _set(step, 'params.inputElements', stepAutoAddedFieldsMap[_get(step,'nodeType')])
        this.isStepSaved = false;
      }
      if (_get(step,'nodeType') === 'OAUTH_STEP_NODE' && _size(_get(step, 'params')) > 1) {
        this.modifyOAuthInputElementsBeforeAssigning(step)
      }
      if (_get(step, 'params.inputElements')) {
        let inputElements = this.modifyInputElementsBeforeAssigning(step.params.inputElements)
        step.params.inputElements = _map(inputElements, (inputElement: any) => {
          const studioInputId = `${step.nodeId}_${_toString(createId().replace('-', ''))}`;
          _set(this.stepInputElementParams, `${step.nodeId}.${studioInputId}`, inputElement)
          return {
            ...inputElement,
            studioInputId: studioInputId
          }
        })
        this.stepDataIdMap.set(step.nodeId, step)
      } else {
        this.stepDataIdMap.set(step.nodeId, step)
      }
    })
    this._stepGraphs$.next(this.stepGraphs);
  }

  modifyOAuthInputElementsBeforeAssigning(step) {
    var keys = Object.keys(_first(stepAutoAddedFieldsMap[_get(step,'nodeType')]))
    _set(step, 'params.inputElements',[])
    step.params.inputElements.push(_pick(step.params, keys))
    step.params = _omit(step.params, keys);
  }

  getCurrentStepNode() {
    return this.stepDataIdMap.get(this.currentStepNodeId);
  }

  generateSourceTypeFromName(name) {
    return name.replace(/ /g, '_').toUpperCase();
  }

  generateStepNodeId(userId, stepIndex = this.currentStepIndex) {
    const timeStamp = new Date().getTime();
    return `STEP_${userId}_${this.studioSourceType}_${stepIndex}_${timeStamp}`
  }

  cleanupTemporaryInputTypes() {
    this.copyFromInputs = _filter(this.copyFromInputs, (inputType)  => !inputType.studioTemp)
  }

  addTemporaryInput(data: StudioFormConfig) {
    const index = _findIndex(this.copyFromInputs, (inputType) => inputType === data);
    this.copyFromInputs.splice(index + 1, 0, {
      ...data,
      studioTemp: true,
    })
  }

  handleDropEvent(event: CdkDragDrop<any>): StudioFormConfig | undefined {
    this.cleanupTemporaryInputTypes()
    const itemData = _get(event, 'item.data')
    const data = _cloneDeep(itemData);
    const studioInputId = `${this.currentStepNodeId}_${createId().replace('-', '')}`;
    try {
      if (event.previousContainer === event.container) {
        moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        return data;
      } else if (_get(event, 'container.data.studioFieldGroup')) {
        // createId().replace('-', '');
        const item: StudioFormConfig = { ...data, studioInputId };
        // event.container.data.splice(event.currentIndex, 0, item);
        // transferArrayItem(
        //   event.previousContainer.data,
        //   item.studioFieldGroup || [],
        //   event.previousIndex,
        //   event.currentIndex
        // )
        event.container.data.fieldGroup = _castArray(event.container.data.fieldGroup)
        transferArrayItem(
          event.previousContainer.data,
          event.container.data.fieldGroup,
          event.previousIndex,
          event.currentIndex
        )
        return event.container.data;
      } else {
        
        const item: StudioFormConfig = { ...data, studioInputId, isNew: true };
        event.container.data.splice(event.currentIndex, 0, item);
        this.focusAndSelectedEditableField({studioInputId, studioInputName: data.studioInputName, studioInputType: data.studioInputType, isNew: true})
        _set(this.temporaryStepGraphFields, `${this.currentStepNodeId}.${studioInputId}`, item)
        return item;
      }
    } finally {
      const ids = _map(event.container.data, 'studioInputId')
      this.stepInputElementOrder[this.currentStepNodeId] = ids;
      this.setFields(event.container.data)
    }
  }

  nextStep() {
    this._changeStep$.next('next');
  }

  previousStep() {
    this._changeStep$.next('previous');
  }

  stepChangeEvent(event) {
    const selectedIndex = event.index;
    const step = this.stepGraphs[selectedIndex];
    this.setCurrentStepNodeId = _get(step, 'nodeId');
    if (!_get(step, 'params.inputElements')) {
      _set(step, 'params.inputElements', stepAutoAddedFieldsMap[_get(step,'nodeType')])
      this.isStepSaved = false;
      let inputElements = this.modifyInputElementsBeforeAssigning(step.params.inputElements)
      step.params.inputElements = _map(inputElements, (inputElement: any) => {
        const studioInputId = `${step.nodeId}_${_toString(createId().replace('-', ''))}`;
        _set(this.stepInputElementParams, `${step.nodeId}.${studioInputId}`, inputElement)
        return {
          ...inputElement,
          studioInputId: studioInputId
        }
      })
      this.stepDataIdMap.set(step.nodeId, step) 
    }
    this._stepChangeEvent$.next({event, currentStep: step});
  }

  setFields(config: StudioFormConfig[] = this.dropInputs) {
    this._fields$.next(JSON.parse(JSON.stringify(config)));
  }

  addItem(config: StudioFormConfig) {
    this.dropInputs = this.dropInputs.concat(config);
    this.setFields();
  }

  focusAndSelectedEditableField(field: StudioFormConfig) {
    this.currentActiveField = _pick(field, ['studioInputId', 'studioInputType', 'isNew', 'props' ])
    this.currentActiveFieldId = field.studioInputId;
    this._currentActiveField$.next(this.currentActiveField);
  }

  updateFieldProperty(currentFieldKey: any, modelParams: any) {
    const propsParamsToBeMerged = ['label', 'placeholder', 'description']
    // get relevant field
    const field = this.dropInputs.find((field) => field.studioInputId === currentFieldKey);
    if (field) {
      _forEach(modelParams, (paramValue, paramKey) => {
        if (!propsParamsToBeMerged.includes(paramKey)) return;
        put(field, `props.${paramKey}`, paramValue)
      })
      this.setFields();
      this.setFieldValues(currentFieldKey, modelParams);      
    }
  }

  setFieldValues(fieldKey, modelParams) {
      this.stepFieldIdMap.set(fieldKey, {...modelParams, studioInputId: fieldKey})
      _set(this.stepInputElementParams, `${this.currentStepNodeId}.${fieldKey}`, modelParams)
  }

  delete(field) {
    _remove(this.dropInputs, (input) => input.studioInputId === field.studioInputId);
    this._fieldDeleteEvent$.next(field);
    if (_includes(studioFormInputTypesWithVariablePool, field.studioInputType)) {
      const fieldParams =  _get(this.stepInputElementParams[this.currentStepNodeId], field.studioInputId)
      const variablePoolKeys = _map(_values(_pick(fieldParams, variablePoolPaths)), item => item.slice(2))
      this.variablePool = _omit(this.variablePool, variablePoolKeys)
    }
    this.stepInputElementParams[this.currentStepNodeId] = _omit(this.stepInputElementParams[this.currentStepNodeId], field.studioInputId)
    this.stepInputElementOrder[this.currentStepNodeId] = _remove(this.stepInputElementOrder[this.currentStepNodeId], (id) => id !== field.studioInputId)
    this.setFields();
  }

  getStepNodeType() {
    return this.stepGraphs.length === 0 ? 'FIRST_STEP_NODE' : 'NORMAL_STEP_NODE'
  }

  getStepData(): Step {
    return {
      nodeId: this.stepDataIdMap.get(this.currentStepIndex),
      type: 'CREATE_NODE',
      "nodePosition": {
        "x": 0,
        "y": 0
      },
      nodeType: this.getStepNodeType(),
      creationTimestamp: _toString(new Date().getTime()),
      params: {
        ...this.stepInputElementParams[this.currentStepNodeId],
        inputElements: _castArray(this.getStepParams())
      }
    }
  }

  getStepParams() {
    console.log(this.stepGraphs[this.stepDataIdMap.get(this.currentStepIndex)])
    return this.stepGraphs[this.stepDataIdMap.get(this.currentStepIndex)]
  }
  
  getCreateNodeEvent(stepData): Step {
    const timeStamp = `${Date.now().toString()}`
    const nodeId = `STEP_${this.userId}_${this.studioSourceType}_${timeStamp}`
    return {
      type: 'CREATE_NODE',
      nodeId,
      nodePosition: {
            "x": 0,
            "y": 0
      },
      nodeType: _get(stepData, 'nodeType'),
      creationTimestamp: timeStamp,
      params: {
        title: _get(stepData, 'title'),
      }
    }
  }

  getDeleteNodeEvent(stepData): Step {
    return {
      type: 'DELETE_NODE',
      nodeId: _get(stepData, 'nodeId'),
      creationTimestamp: _toString(new Date().getTime()),
    }
  }

  createNewStep(event): Observable<any> {
    const userId = this.userId
    const sourceType = this.studioSourceType    
    const createNode$ = this.stepsBuilderDataService.updateStepGraph({userId, sourceType, data: _castArray(event)})
    return createNode$.pipe(takeUntil(this.unsubscribe$))
  }

  addStepGraph(stepGraph) {
    this.stepGraphs.push(stepGraph);
    this.stepDataIdMap.set(stepGraph.nodeId, stepGraph)
    this._addStepGraphs$.next(this.stepGraphs);
  }

  deleteStep(event): Observable<any> {
    const userId = this.userId
    const sourceType = this.studioSourceType
    const deleteNode$ = this.stepsBuilderDataService.updateStepGraph({userId, sourceType, data: _castArray(event)})
    return deleteNode$.pipe(takeUntil(this.unsubscribe$))
  }

  getSourceInfoUpdateEvent(sourceInfo) {
    const userId = this.userId
    const sourceType = this.generateSourceTypeFromName(sourceInfo.dataSourceName);
    const uploadDataSourceInfo$ = this.stepsBuilderDataService.uploadDataSourceInfo({
      userId,
      sourceType: sourceType,
      data: sourceInfo
    })
    return uploadDataSourceInfo$;
  }

  deleteStepGraph(stepGraph) {
    _remove(this.stepGraphs, (graph) => graph.nodeId === stepGraph.nodeId);
    this.stepDataIdMap.delete(stepGraph.nodeId)
    this._stepGraphs$.next(this.stepGraphs);
  }

  getStepUpdateEvent(stepData, topLevelParams, inputElements): Step{
    console.log(stepData);
    if (_get(stepData, "nodeType") === "OAUTH_STEP_NODE") {
      let oauthInputElements:{} = _first(inputElements)
      return {
        type: 'EDIT_NODE_PARAMS',
        nodeId: stepData.nodeId,
        creationTimestamp: _toString(new Date().getTime()),
        updatedParamsValues: {
          ...topLevelParams,
          ...oauthInputElements
        },
      };
    } else {
      return {
        type: 'EDIT_NODE_PARAMS',
        nodeId: stepData.nodeId,
        creationTimestamp: _toString(new Date().getTime()),
        updatedParamsValues: {
          ...topLevelParams,
          inputElements: _castArray(inputElements),
        },
      };
    }
  }

  getVariablePoolUpdateEvent(updatedVariablePool?): Step {
    return {
      type: 'SAVE_VARIABLE_POOL',
      variablePool: updatedVariablePool || this.variablePool,
      creationTimestamp: _toString(new Date().getTime()),
    }
  }

  saveEventListener(type) {
    this._saveStepEvent$.next(type);
  }
  
  validateESTEventListener(data) {
    this._validateEST$.next(data);
  }

  saveStepGraphAsDraft(event) {
    const updateStep$ =  this.stepsBuilderDataService.updateStepGraph({
      userId: this.userId,
      sourceType: this.studioSourceType,
      data: _castArray(event)
    })
    return updateStep$.pipe(takeUntil(this.unsubscribe$))
  }
  
  saveVariablePool() {
    const saveVariablePoolEvent = this.getVariablePoolUpdateEvent(!_isEmpty(this.updatedVariablePool)? this.updatedVariablePool : this.variablePool);
    const saveVariablePoolEvent$ = this.saveStepGraphAsDraft(saveVariablePoolEvent);
    saveVariablePoolEvent$.subscribe({
      next: (data) => {
        this.snackBar.open('Variable pool saved successfully', '', {
          duration: 2000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
      },
      error: (error) => {
        const errorMessage = _get(error, 'error.message')
        this.snackBar.open(errorMessage || 'Error in fetching variable pool', '', {
          duration: 2000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
      }
    })
  }

  modifyInputElementsBeforeSave(inputElements, temporaryInputElements) {
    const immutableInputElements = _cloneDeep(inputElements);
    let immutableTemporaryInputElements = _cloneDeep(temporaryInputElements);
    const ignoreFields = _keys(immutableInputElements)
    immutableTemporaryInputElements = _omit(immutableTemporaryInputElements, ignoreFields)
    const allInputElements = {...immutableInputElements, ...immutableTemporaryInputElements}
    let sortedElements = []
    _forEach(allInputElements, (element, key) => {
      if (element.isNew === true && _has(element, 'props')) {
        const type =  _get(element, 'studioInputType')
        element = {
          type: type,
        }
      }
      element = this.generateVariablePoolFromInputFieldValues(element, element.type)
      sortedElements[_indexOf(this.stepInputElementOrder[this.currentStepNodeId], key)] = element
    })
    return sortedElements;
  }

  modifyInputElementsBeforeAssigning(inputElements) {
    const immutableInputElements = _compact(_cloneDeep(inputElements));
    _forEach(immutableInputElements, (element: any, key, val) => {
      if (element && element.type) {
        element = this.generateInputFieldOptionsFromVariablePool(element, element.type)
      }
    })
    return immutableInputElements;
  }

  sortInputElements(inputElements) {
    let sortedElements = []
    _forEach(inputElements, (element) => {
      sortedElements[_indexOf(this.stepInputElementOrder[this.currentStepNodeId], element.studioInputId)] = element
    })
    return sortedElements;
  }

  generateVariablePoolFromInputFieldValues(field, type) {
    switch (type) {
      case 'FIXED_VALUES_SLIDER':
      case 'CHECKBOX':
      case 'DROPDOWN_LIST':
      case 'RADIO_BUTTON':
          const fieldOptions = _get(field, 'options');
          const fieldId  = _get(field, 'id');
          field = _omit(field, ['options']);
          // generate replication_frequency_labels and replication_frequency_values from fixedValuesSliderOptions
          let fieldLabels = []
          let fieldValues = []
          if (!_isEmpty(_compact(fieldOptions))) {
            _forEach(fieldOptions, (option) => {
              fieldLabels.push(option.key);
              fieldValues.push(option.value);
            })
            _set(this.variablePool, `${field.id}_labels`, fieldLabels);
            _set(this.variablePool, `${field.id}_values`, fieldValues);
          } else if (!_isUndefined(fieldId)) {
            _set(this.variablePool, `${field.id}_labels`, []);
            _set(this.variablePool, `${field.id}_values`, []);
          }
        break;
      case 'ACCORDION_GROUP': 
          const childElements =  _get(field, 'childInputElements');
          if (!_isEmpty(_compact(childElements))) {
            _forEach(childElements, (element, index) => {
              element = this.generateVariablePoolFromInputFieldValues(element, element.type)
              childElements[index] = element;
            })
          }
          field.childInputElements = childElements;
        break;
    
      default:
        break;
    }
    return field;
  }

  generateInputFieldOptionsFromVariablePool(field, type) {
    switch (type) {
      case 'FIXED_VALUES_SLIDER':
      case 'CHECKBOX':
      case 'DROPDOWN_LIST':
      case 'RADIO_BUTTON':
        const fieldLabels = _get(this.variablePool, `${field.id}_labels`);
        const fieldValues = _get(this.variablePool, `${field.id}_values`);
        if (!_isUndefined(fieldLabels) || !_isUndefined(fieldValues)) {
          let fieldOptions = []
          _forEach(fieldLabels, (label, index) => {
              fieldOptions.push({
                key: label,
                value: _get(fieldValues, index)
              })
          })
          field = _mergeWith(field, {options: fieldOptions}, (objValue, srcValue) => {
            if (_isArray(objValue)) {
              return objValue.concat(srcValue);
            }
          });
        }
        break;
      case 'ACCORDION_GROUP':
        const childElements =  _get(field, 'childInputElements');
        if (!_isEmpty(_compact(childElements))) {
          _forEach(childElements, (element) => {
            element = this.generateInputFieldOptionsFromVariablePool(element, element.type)
          })
        }
        break;
      default:
        break;
    }
    return field;
  }

  omitFieldsFromTemporaryStepFields(stepNodeId) {
    const inputElements = this.temporaryStepGraphFields[stepNodeId];
    _forEach(inputElements, (element, key) => {
      if (element.isNew === true && !_has(element, 'props')) {
        delete this.temporaryStepGraphFields[stepNodeId][key]
      }
    })
  }

  builderClickHandler(event) {
    this._builderClickEvent$.next(event);
  }

  addFieldThroughClick(inputElement, parentField) {
    const data = _cloneDeep(StudioFormInputElementTypes[5]);
    const studioInputId = `${this.currentStepNodeId}_${createId().replace('-', '')}`;
    const item: StudioFormConfig = { ...data, studioInputId, isNew: true };
    parentField.studioFieldGroup.push(item)
    this.focusAndSelectedEditableField({studioInputId, studioInputName: data.studioInputName, studioInputType: data.studioInputType, isNew: true})
    _set(this.temporaryStepGraphFields, `${this.currentStepNodeId}.${studioInputId}`, item)
    this.setFields();

    // {
    //   previousContainer: null,
    //   container: {
    //     data: this.dropInputs,
    //     id: 'cdk-drop-list-0',
    //     element: null,
    //     connectedTo: [],
    //     disabled: false
    //   },
    //   item: {
    //     data: data
    //   },
    //   currentIndex: 0,
    //   previousIndex: 0,
    //   isPointerOverContainer: true,
    //   distance: {
    //     x: 0,
    //     y: 0
    //   }
    // }
    // const event: CdkDragDrop<any> = {}
    // this.handleDropEvent(event)
  }

  saveSourceInfoEventListener() {
    this._saveSourceInfoEvent$.next();
  }
}